import { MODAL_SOURCE } from '../../analytics.ts';

import type { ConnectorPromoBannerVariant } from './ConnectorPromoBanner.tsx';

export const PROMO_BANNER_ACTION_SUBJECT_ID = 'connectorPromoBanner';

/**
 * Why the Google Drive variant lost, in check order. No `dismissed` or `noAuthUrl` — this
 * surface has no dismissal, and the banner opens the connector dialog rather than needing
 * an auth URL of its own.
 */
export type GdriveIneligibleReason =
	| 'notFirstPartyOnly'
	| 'noSiteWideDriveLinks'
	| 'alreadyAuthenticated';

/** Why the org variant lost, in check order. */
export type OrgIneligibleReason = 'noUnconnectedThirdParty' | 'noTopApp';

export type PromoBannerAttributes = {
	bannerType: ConnectorPromoBannerVariant;
	/**
	 * Set whenever the Drive variant did not win, so a `generic` or `orgConnectedApps`
	 * banner still says why Drive was ruled out. Absent on the Drive variant itself.
	 */
	gdriveIneligibleReason?: GdriveIneligibleReason;
	/** Likewise for the org variant; absent when the org variant won. */
	orgIneligibleReason?: OrgIneligibleReason;
	/** Absent on the generic variant, which is about no app in particular. */
	connector?: string;
	/** Only set on the org variant. */
	topAppPercent?: number;
	/** Icons on screen when the event fired; they can arrive after it. */
	ghostIconCount: number;
};

type PromoBannerUIEvent<TAction extends string, TActionSubject extends string> = {
	readonly type: 'sendUIEvent';
	readonly data: {
		readonly action: TAction;
		readonly actionSubject: TActionSubject;
		readonly actionSubjectId: 'connectorPromoBanner';
		readonly source: 'teamworkGraphModal';
		readonly attributes: PromoBannerAttributes;
	};
};

export const getBannerShownEvent = (
	attributes: PromoBannerAttributes,
): PromoBannerUIEvent<'viewed', 'banner'> =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'viewed',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;

export const getBannerClickedEvent = (
	attributes: PromoBannerAttributes,
): PromoBannerUIEvent<'clicked', 'banner'> =>
	({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;

/**
 * The whole surface is suppressed, as opposed to a variant losing — the banner has
 * nothing to connect to when the dialog it opens is unavailable.
 */
export type BannerNotShownReason = 'rovoDisabled';

export type BannerNotShownAttributes = {
	reason: BannerNotShownReason;
};

type PromoBannerOperationalEvent<TAction extends string, TActionSubject extends string> = {
	readonly type: 'sendOperationalEvent';
	readonly data: {
		readonly action: TAction;
		readonly actionSubject: TActionSubject;
		readonly actionSubjectId: 'connectorPromoBanner';
		readonly source: 'teamworkGraphModal';
		readonly attributes: BannerNotShownAttributes;
	};
};

export const getBannerNotShownEvent = (
	attributes: BannerNotShownAttributes,
): PromoBannerOperationalEvent<'notShown', 'banner'> =>
	({
		type: 'sendOperationalEvent',
		data: {
			action: 'notShown',
			actionSubject: 'banner',
			actionSubjectId: PROMO_BANNER_ACTION_SUBJECT_ID,
			source: MODAL_SOURCE,
			attributes,
		},
	}) as const;
